import type { Customer, PaymentMethod, SavedCard } from '@/types/checkout';
import { CardType } from '@/types/common';
import type { PaymentMethod as StripePaymentMethod } from '@stripe/stripe-js';
import { getCustomerCountry } from './device';

export const getCardType = (cardNumber: string): CardType | null => {
  const cleanNumber = cardNumber.replace(/\D/g, '');

  if (cleanNumber.length < 4) return null;

  if (cleanNumber.startsWith('4')) return CardType.VISA;

  if (cleanNumber.startsWith('5') && cleanNumber[1] >= '1' && cleanNumber[1] <= '5') return CardType.MASTERCARD;
  if (cleanNumber.startsWith('2') && cleanNumber[1] >= '2' && cleanNumber[1] <= '7') return CardType.MASTERCARD;

  if (cleanNumber.startsWith('34') || cleanNumber.startsWith('37')) return CardType.AMEX;

  if (cleanNumber.startsWith('6011')) return CardType.DISCOVER;
  if (cleanNumber.startsWith('65')) return CardType.DISCOVER;
  if (cleanNumber.startsWith('64') && cleanNumber[2] >= '4' && cleanNumber[2] <= '9') return CardType.DISCOVER;

  return CardType.VISA;
};

export const buildBraintreeSavedCreditCardPaymentRequest = (
  selectedCard: SavedCard,
  customer: Customer,
  paymentMethod: PaymentMethod
) => {
  return {
    paymentProvider: selectedCard.paymentProvider,
    receipt_email: customer.email,
    paymentMethodDisplayName: 'Credit Card',
    paymentMethodId: paymentMethod.id,
    params: {
      paymentToken: selectedCard.pspCardIdentifier,
      fraudDetection: {},
    },
  };
};

export const buildStripeSavedCreditCardPaymentRequest = (
  selectedCard: SavedCard,
  customer: Customer,
  paymentMethod: PaymentMethod
) => {
  return {
    paymentProvider: 'stripe',
    receipt_email: customer.email,
    paymentMethodDisplayName: 'Credit Card',
    paymentMethodId: paymentMethod.id,
    params: {
      paymentMethodId: selectedCard.pspCardIdentifier,
      stripeCustomerId: selectedCard.pspCustomerId,
      fraudDetection: {
        forterTokenCookie: '',
      },
    },
  };
};

export const buildBraintreeCreditCardPaymentRequest = (
  _selectedCard: SavedCard,
  _paymentMethod: PaymentMethod,
  internalPaymentMethodId: string,
  receipt_email: string
) => {
  return {
    paymentProvider: 'braintree',
    paymentMethodId: internalPaymentMethodId,
    paymentMethodDisplayName: 'Credit Card',
    receipt_email: receipt_email,
  };
};

export const buildStripeCreditCardPaymentRequest = (
  paymentMethod: StripePaymentMethod,
  internalPaymentMethodId: string,
  receipt_email: string,
  shouldSaveCard: boolean
) => {
  const card = paymentMethod.card!;

  return {
    paymentProvider: 'stripe',
    receipt_email: receipt_email,
    params: {
      paymentMethodId: paymentMethod.id,
      cardDetails: {
        bin: '',
        nameOnCard: '',
        lastFourDigits: card.last4,
        expirationMonth: card.exp_month.toString(),
        expirationYear: card.exp_year.toString(),
        cardBrand: card.brand,
        fingerprint: '',
        countryOfIssuance: card.country || '',
      },
      paymentMethodDetails: {
        id: paymentMethod.id,
        object: 'payment_method',
        allow_redisplay: paymentMethod.allow_redisplay || 'unspecified',
        billing_details: {
          address: {
            city: paymentMethod.billing_details?.address?.city ?? null,
            country: paymentMethod.billing_details?.address?.country ?? null,
            line1: paymentMethod.billing_details?.address?.line1 ?? '',
            line2: paymentMethod.billing_details?.address?.line2 ?? null,
            postal_code: paymentMethod.billing_details?.address?.postal_code ?? '',
            state: paymentMethod.billing_details?.address?.state ?? null,
          },
          email: receipt_email,
          name: paymentMethod.billing_details?.name ?? null,
          phone: paymentMethod.billing_details?.phone ?? null,
          tax_id: null,
        },
        card: {
          brand: card.brand,
          checks: {
            address_line1_check: card.checks?.address_line1_check ?? null,
            address_postal_code_check: card.checks?.address_postal_code_check ?? null,
            cvc_check: card.checks?.cvc_check ?? null,
          },
          country: card.country || '',
          display_brand: card.display_brand || card.brand,
          exp_month: card.exp_month,
          exp_year: card.exp_year,
          funding: card.funding,
          generated_from: null,
          last4: card.last4,
          networks: {
            available: card.networks?.available || [card.brand],
            preferred: card.networks?.preferred ?? null,
          },
          regulated_status: 'unregulated',
          three_d_secure_usage: {
            supported: card.three_d_secure_usage?.supported ?? true,
          },
          wallet: card.wallet ?? null,
        },
        created: paymentMethod.created,
        customer: paymentMethod.customer as string | null,
        livemode: paymentMethod.livemode,
        radar_options: {},
        type: 'card',
      },
      shouldSaveCard: shouldSaveCard,
      fraudDetection: {
        forterTokenCookie: '',
      },
    },
    paymentMethodDisplayName: 'Credit Card',
    paymentMethodId: internalPaymentMethodId,
  };
};

export const buildStripeNewCardPaymentRequest = (
  selectedCard: SavedCard,
  customer: Customer,
  paymentMethod: PaymentMethod
) => {
  return {
    paymentProvider: 'stripe',
    receipt_email: customer.email,
    paymentMethodDisplayName: 'Credit Card',
    paymentMethodId: paymentMethod.id,
    params: {
      paymentMethodId: selectedCard.pspCardIdentifier,
      cardDetails: {
        bin: '',
        nameOnCard: selectedCard.nameOnCard ?? selectedCard.cardholderName ?? '',
        lastFourDigits: selectedCard.cardLastFourDigits ?? '',
        expirationMonth: selectedCard.expiryMonth ?? '',
        expirationYear: selectedCard.expiryYear ?? '',
        cardBrand: selectedCard.brand ?? '',
        fingerprint: '',
        countryOfIssuance: getCustomerCountry(),
      },
      paymentMethodDetails: {
        id: selectedCard.pspCardIdentifier,
        object: 'payment_method',
        billing_details: {
          email: customer.email,
          phone: null,
          tax_id: null,
        },
        card: {
          brand: selectedCard.brand ?? '',
          checks: {
            address_line1_check: null,
            address_postal_code_check: null,
            cvc_check: null,
          },
          country: getCustomerCountry(),
          display_brand: selectedCard.brand ?? '',
          exp_month: selectedCard.expiryMonth ?? '',
          exp_year: selectedCard.expiryYear ?? '',
          funding: 'credit',
          generated_from: null,
          last4: selectedCard.cardLastFourDigits ?? '',
          networks: {
            available: [selectedCard.brand ?? ''],
            preferred: null,
          },
          regulated_status: 'unregulated',
          three_d_secure_usage: {
            supported: true,
          },
          wallet: null,
        },
        created: Date.now(),
        customer: null,
        livemode: false,
        radar_options: {},
        type: 'card',
      },
      shouldSaveCard: true,
      fraudDetection: {
        forterTokenCookie: '',
      },
    },
  };
};
